/* biome-ignore-all lint/suspicious/noExplicitAny: reading practice logs */
import type React from "react"
import { Table, Tag, Typography, Tooltip, Button, Progress, type TableProps, type TablePaginationConfig } from "antd"
import { EyeOutlined } from "@ant-design/icons"
import { UserCellLink } from "@/components/UserCellLink"
import { useTableScroll } from "@/hooks/useTableScroll"
import type { ReadingPracticeLogItem } from "../types"
import dayjs from "dayjs"
import "@/styles/table.full.css"

const { Text } = Typography

export const DIFFICULTY_MAP: Record<string, { label: string; color: string }> = {
  easy: { label: "Dễ", color: "green" },
  medium: { label: "Trung bình", color: "gold" },
  hard: { label: "Khó", color: "volcano" },
}

export const QUESTION_TYPE_MAP: Record<string, { label: string; color: string }> = {
  cloze: { label: "Cloze", color: "blue" },
  mcq: { label: "Trắc nghiệm", color: "purple" },
  tf: { label: "Đúng / Sai", color: "cyan" },
}

interface ReadingPracticeTableProps {
  data: ReadingPracticeLogItem[]
  loading: boolean
  onViewDetail: (record: ReadingPracticeLogItem, index: number) => void
  pagination?: TableProps<ReadingPracticeLogItem>["pagination"]
}

export const ReadingPracticeTable: React.FC<ReadingPracticeTableProps> = ({
  data,
  loading,
  onViewDetail,
  pagination,
}) => {
  const { scrollConfig, tableWrapperRef } = useTableScroll()

  const columns: TableProps<ReadingPracticeLogItem>["columns"] = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 75,
      align: "center",
      fixed: "left",
      render: (val: number) => <span className="font-mono text-gray-500 text-xs">#{val}</span>,
    },
    {
      title: "Trạng thái",
      dataIndex: "status",
      key: "status",
      width: 95,
      align: "center",
      render: (status: string, record) => {
        const isCompleted = status === "completed" || (!status && !!record.completed_at)
        if (isCompleted) {
          return (
            <Tag color="success" className="m-0 font-medium text-xs">
              HT
            </Tag>
          )
        }
        return (
          <Tag color="warning" className="m-0 font-medium text-xs">
            Chưa HT
          </Tag>
        )
      },
    },
    {
      title: "Người dùng",
      key: "user",
      width: 200,
      fixed: "left",
      render: (_, record) => (
        <UserCellLink user={record.user} userId={record.user_id} fallback={<Tag color="default">Khách</Tag>} />
      ),
    },
    {
      title: "Bài đọc & Bộ từ",
      key: "reading_content",
      width: 240,
      render: (_, record) => {
        const rc = record.reading_content
        if (!rc) return <Text type="secondary">—</Text>

        const title = rc.title || `Bài đọc #${rc.id}`
        const setTitle = rc.set?.full_title || rc.set?.title
        const setId = rc.set?.id

        const setSlug = rc.set?.slug
        const setUrl = setId
          ? setSlug
            ? `https://jupi.vn/set/${setSlug}-${setId}`
            : `https://jupi.vn/set/${setId}`
          : null

        return (
          <div className="flex flex-col gap-0.5 leading-tight">
            <div className="flex items-center gap-1.5">
              <span className="max-w-[190px] truncate font-semibold text-gray-800 text-xs" title={title}>
                {title}
              </span>
              <Tag color="cyan" className="m-0 px-1 py-0 text-[10px] leading-tight">
                Part {rc.part_number}
              </Tag>
            </div>
            {setTitle && (
              <span className="max-w-[210px] truncate text-[11px] text-gray-500" title={setTitle}>
                Bộ:{" "}
                {setUrl ? (
                  <a
                    href={setUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="font-semibold text-primary hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {setTitle}
                  </a>
                ) : (
                  <strong>{setTitle}</strong>
                )}
              </span>
            )}
          </div>
        )
      },
    },
    {
      title: "Độ khó",
      key: "difficulty",
      width: 120,
      align: "center",
      render: (_, record) => {
        const diff = record.reading_content?.difficulty || ""
        const info = DIFFICULTY_MAP[diff]
        if (info) {
          return (
            <Tag color={info.color} className="m-0 font-medium text-xs">
              {info.label}
            </Tag>
          )
        }
        return <Tag className="m-0 text-xs">{diff || "—"}</Tag>
      },
    },

    {
      title: "Thời gian làm bài",
      dataIndex: "time_spent_seconds",
      key: "time_spent_seconds",
      width: 130,
      align: "center",
      render: (seconds: number | null | undefined) => {
        if (seconds == null || Number.isNaN(seconds)) return <Text type="secondary">—</Text>
        const mins = Math.floor(seconds / 60)
        const secs = seconds % 60
        const formatted = `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`
        return <span className="font-mono font-semibold text-gray-700 text-xs">{formatted}</span>
      },
    },

    {
      title: "Kết quả bài làm",
      key: "score_result",
      width: 170,
      align: "center",
      render: (_, record) => {
        const isInit = record.status === "init" || (!record.status && !record.completed_at)
        if (isInit) {
          return <span className="text-gray-400 text-xs italic">Chưa nộp bài</span>
        }

        const correct = record.correct_count ?? 0
        const wrong = record.wrong_count ?? 0
        const skipped = record.skipped_count ?? 0
        const total = correct + wrong + skipped
        const accuracy = total > 0 ? Math.round((correct / total) * 100) : 0

        const accuracyColor = accuracy >= 80 ? "text-emerald-600" : accuracy >= 50 ? "text-amber-600" : "text-rose-600"

        return (
          <Tooltip
            title={
              <div className="flex flex-col gap-1 py-0.5 text-xs">
                <div>
                  • <strong>Đúng:</strong> {correct} câu
                </div>
                <div>
                  • <strong>Sai:</strong> {wrong} câu
                </div>
                <div>
                  • <strong>Bỏ qua:</strong> {skipped} câu
                </div>
                <div>
                  • <strong>Tổng số:</strong> {total} câu
                </div>
                <div>
                  • <strong>Độ chính xác:</strong> {accuracy}%
                </div>
              </div>
            }
          >
            <div className="flex cursor-pointer select-none flex-col items-center justify-center">
              <div className="flex items-baseline gap-1 font-mono text-xs">
                <span className={`font-bold ${accuracyColor}`}>{accuracy}%</span>
                <span className="text-gray-400">
                  ({correct}/{total})
                </span>
              </div>
              <div className="mt-1 w-24">
                <Progress
                  percent={accuracy}
                  size="small"
                  showInfo={false}
                  status={accuracy >= 80 ? "success" : accuracy >= 50 ? "normal" : "exception"}
                />
              </div>
            </div>
          </Tooltip>
        )
      },
    },

    {
      title: "Thời gian",
      key: "times",
      width: 190,
      align: "center",
      render: (_, record) => {
        const start = record.created_at ? dayjs(record.created_at) : null
        const end = record.completed_at ? dayjs(record.completed_at) : null
        return (
          <div className="flex flex-col pl-2 text-left font-mono text-xs leading-tight">
            <div>
              <span className="text-gray-400">Bắt đầu: </span>
              <span className="text-gray-700">{start ? start.format("DD/MM/YYYY HH:mm:ss") : "—"}</span>
            </div>
            <div>
              <span className="text-gray-400">Nộp bài: </span>
              <span className={end ? "font-medium text-gray-700" : "text-gray-400"}>
                {end ? end.format("DD/MM/YYYY HH:mm:ss") : "—"}
              </span>
            </div>
          </div>
        )
      },
    },
    {
      title: "Thao tác",
      key: "actions",
      align: "center",
      width: 100,
      fixed: "right",
      render: (_, record, index) => (
        <Button
          size="small"
          type="primary"
          ghost
          icon={<EyeOutlined />}
          onClick={() => onViewDetail(record, index)}
          className="px-2 text-xs"
        >
          Chi tiết
        </Button>
      ),
    },
  ]

  return (
    <div className="min-h-0 min-w-0 flex-1" ref={tableWrapperRef}>
      <Table<ReadingPracticeLogItem>
        bordered
        rowKey="id"
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={pagination as TablePaginationConfig}
        scroll={{ x: "100%", y: scrollConfig.y }}
        size="small"
        classNames={{
          root: "full-height-table",
        }}
      />
    </div>
  )
}

export default ReadingPracticeTable
